import { baseApi } from 'api/api-base';
import { API_ROUTES } from 'constants/api-routes';
import { DEFAULT_PAGE_SIZE } from 'constants/general';
import {
  ExecutePayment,
  GetExchangeRate,
  GetGateways,
  GetInvoices,
  GetPaymentMethods,
  GetTransactions,
  PostInvoices,
  SignContract,
} from 'types/api/api-balance.types';
import { PaymentType } from 'types/payment.types';

// crypto/stripe each have a dedicated create-payment route; fiat and klarna both post to Mollie's
// route (CREATE_FIAT_PAYMENT), same as before this map existed.
const PAYMENT_TYPE_ROUTES: Partial<Record<PaymentType, string>> = {
  crypto: API_ROUTES.CREATE_CRYPTO_PAYMENT,
  stripe: API_ROUTES.CREATE_STRIPE_PAYMENT,
};

export const balanceApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    createInvoice: builder.mutation<PostInvoices.ResponseType, PostInvoices.RequestType>({
      query: body => ({
        method: 'POST',
        url: `${API_ROUTES.POST_INVOICE}`,
        body,
      }),
      extraOptions: {},
    }),

    executePayment: builder.mutation<ExecutePayment.ResponseType, ExecutePayment.RequestType>({
      query: ({ amount, productId, isKlarna, paymentType }) => ({
        method: 'POST',
        url: PAYMENT_TYPE_ROUTES[paymentType] ?? API_ROUTES.CREATE_FIAT_PAYMENT,
        body: { amount: Number(amount), productId, isKlarna },
      }),
      extraOptions: {},
    }),

    signContract: builder.mutation<Blob, SignContract.RequestType>({
      query: ({ hostingFee, orderId, productId, sign }) => ({
        url: API_ROUTES.SIGN_CONTRACT,
        method: 'POST',
        body: { hostingFee, orderId, productId, sign },
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/pdf',
        },
        responseHandler: async (response: Response) => {
          const ct = response.headers.get('content-type') || '';
          if (ct.includes('pdf')) return response.blob();
          return response.json();
        },
      }),
      extraOptions: {},
    }),

    getInvoices: builder.query<GetInvoices.ResponseType, GetInvoices.RequestType>({
      query: () => ({
        url: API_ROUTES.GET_INVOICES,
      }),
      extraOptions: {},
    }),

    downloadInvoice: builder.query<Blob, string>({
      query: (id: string) => ({
        url: `invoice/${id}`,
        method: 'GET',
        headers: { Accept: 'application/pdf' },
        responseHandler: async response => response.blob(),
      }),
      extraOptions: {},
      serializeQueryArgs: () => '',
      keepUnusedDataFor: 0,
    }),

    // getWallets: builder.query<GetWallets.ResponseType, GetWallets.RequestType>({
    //   query: ({ page = 0, size = DEFAULT_PAGE_SIZE, sort }) => ({
    //     url: `${API_ROUTES.GET_WALLETS}`,
    //     params: { page, size, sort },
    //   }),
    //   extraOptions: {},
    // }),
    getGetaways: builder.query<GetGateways.ResponseType, GetGateways.RequestType>({
      query: ({ page = 0, size = DEFAULT_PAGE_SIZE, sort }) => ({
        url: `${API_ROUTES.GET_GATEWAYS}`,
        params: { page, size, sort },
      }),
      extraOptions: {},
    }),
    getPaymentMethods: builder.query<GetPaymentMethods.ResponseType, GetPaymentMethods.RequestType>({
      query: ({ page = 0, size = DEFAULT_PAGE_SIZE, sort }) => ({
        url: `${API_ROUTES.GET_PAYMENT_METHODS}`,
        params: { page, size, sort },
      }),
      extraOptions: {},
    }),
    getTransactions: builder.query<GetTransactions.ResponseType, GetTransactions.RequestType>({
      query: ({ page = 0, size = DEFAULT_PAGE_SIZE, sort, currency, financialAccounts }) => ({
        url: `${API_ROUTES.GET_TRANSACTIONS}`,
        params: { page, size, sort, currency, financialAccounts },
      }),
      extraOptions: {},
    }),
    getExchangeRate: builder.query<GetExchangeRate.ResponseType, GetExchangeRate.RequestType>({
      query: () => ({
        url: `${API_ROUTES.GET_BTC_RATE}`,
      }),
      extraOptions: {},
    }),
  }),
});

export const {
  useGetInvoicesQuery,
  useLazyDownloadInvoiceQuery,
  // useGetWalletsQuery,
  useGetTransactionsQuery,
  useGetGetawaysQuery,
  useGetPaymentMethodsQuery,
  useExecutePaymentMutation,
  useSignContractMutation,
  useGetExchangeRateQuery,
} = balanceApi;
